import { VarianteChip } from '../ui/primitivos/Chip';
import { TipoRegistroEnvio } from '../api/associado/associadoApi';
import { DivergenciaOrigem, DivergenciaStatus } from '../api/sga-divergencia/sgaDivergenciaApi';
import { EnvioLogItem } from '../api/sga-envio-log/sgaEnvioLogApi';

/**
 * Rótulos do acompanhamento de envios ao SGA (at-70, frente D/D8).
 *
 * 🔴 **Existe porque a mesma regra vivia em TRÊS componentes e numa bolinha.** A
 * tela `/ti/envios` marcava o estado de envio com um `<span>` de 10px colorido
 * (`Dot`, verde ou vermelho) cujo único texto vivia num `title`; os mapas
 * `TIPO_REGISTRO`, `STATUS_DIVERGENCIA` e `ORIGEM_DIVERGENCIA` eram **exportados de
 * dentro de modais** e importados de volta pela tela; e o `ChipEnvio` era exportado
 * de um quarto arquivo. Regra repetida em componente é divergência com data
 * marcada (convenção da at-24), e regra exportada de modal inverte a dependência.
 *
 * ⚠️ **Todas as funções devolvem `{ texto, variante }` do `Chip`** — o padrão de
 * `rotuloStatus` (at-56), `rotuloEstadoVistoria` (at-63) e `rotuloStatusExecucao`
 * (at-67). É o que garante que estado nunca seja **só cor**: o `Chip` exige texto
 * (spec 02 §9, decisão de Victor na at-54).
 *
 * ⚠️ **Pendência é ÂMBAR, nunca vermelho.** "Ainda não foi ao SGA" não é erro — o
 * envio é automático e o cron reenvia a cada 2 horas (spec 02 §2.3). Vermelho fica
 * para o que terminou mal: uma falha de envio no log.
 *
 * ⚠️ **Valor desconhecido devolve o PRÓPRIO valor em `neutro`**, nunca um chip
 * mudo: se o backend passar a emitir um status novo, a tela mostra o que recebeu
 * em vez de esconder — mesmo princípio da situação do SGA, que o SAAC relata sem
 * interpretar (at-32).
 */

export interface RotuloChip {
  texto: string;
  variante: VarianteChip;
}

/* ───────────────────────── envio ao SGA ───────────────────────── */

/**
 * Estado de envio de um registro.
 *
 * `longo` é para o modal de detalhes, onde há espaço para a frase inteira; a
 * tabela usa o curto, que precisa caber numa coluna ao lado de outros chips. Os
 * dois textos são os que a tela já usava (`ChipEnvio` × as colunas).
 */
export function rotuloEnvioRegistro(
  enviado: boolean,
  formato: 'curto' | 'longo' = 'curto',
): RotuloChip {
  if (enviado) {
    return { texto: formato === 'longo' ? 'Cadastrado no SGA' : 'No SGA', variante: 'ok' };
  }
  return {
    texto: formato === 'longo' ? 'Envio ao SGA pendente' : 'Envio pendente',
    variante: 'pendente',
  };
}

/**
 * A nota que acompanha qualquer pendência de envio nesta tela.
 *
 * 📌 Estava só no `title` do `ChipEnvio` (*"o cron reenvia a cada 2h"*) — e `title`
 * não existe no celular (at-54). É a mesma frase do `ModalDetalhesAdesao` e do
 * `ModalDetalhesCarteira`: dizer que o envio é automático evita que pendência
 * vire susto.
 */
export const NOTA_ENVIO_AUTOMATICO =
  'O envio ao SGA é automático: o sistema tenta na hora e repete a cada 2 horas. Pendência aqui não exige ação imediata.';

/* ───────────────────── tipo do registro (at-28) ───────────────────── */

export interface RotuloTipoRegistro extends RotuloChip {
  /** O que era o `title` da pílula — agora aparece em texto no modal. */
  descricao: string;
}

/**
 * ⚠️ **Os três tipos de substituição compartilham a variante `marca`.** Antes cada
 * um tinha uma cor própria (laranja, azul, roxo), o que exigia três cores fora dos
 * tokens para distinguir categorias. Quem distingue é o **texto** — e o design
 * system reserva cor para significado (ok / pendente / perigo), não para
 * categoria. "Adesão" fica `neutro` porque é o caso comum, não um destaque.
 */
const POR_TIPO_REGISTRO: Record<TipoRegistroEnvio, RotuloTipoRegistro> = {
  ADESAO: {
    texto: 'Adesão',
    variante: 'neutro',
    descricao: 'Cadastro novo, sem substituição.',
  },
  SUBSTITUICAO_VEICULO: {
    texto: 'Substituição',
    variante: 'marca',
    descricao: 'Veículo substituiu outro do mesmo associado.',
  },
  TROCA_TITULAR: {
    texto: 'Troca de titular',
    variante: 'marca',
    descricao: 'Veículo passou para outro titular.',
  },
  SUBSTITUICAO_E_TROCA_TITULAR: {
    texto: 'Substituição + titular',
    variante: 'marca',
    descricao: 'Veículo substituiu outro e mudou de titular.',
  },
};

export function rotuloTipoRegistro(tipo: TipoRegistroEnvio | string): RotuloTipoRegistro {
  return (
    POR_TIPO_REGISTRO[tipo as TipoRegistroEnvio] ?? {
      texto: String(tipo),
      variante: 'neutro' as VarianteChip,
      descricao: '',
    }
  );
}

/**
 * A placa antiga só faz sentido nos tipos que de fato substituíram um veículo.
 *
 * 📌 Veio **verbatim** do `TipoRegistroBadge`, que morreu nesta branch.
 */
export function mostraPlacaAntiga(carro: {
  placaAntiga: string | null;
  tipo: TipoRegistroEnvio;
}): boolean {
  return Boolean(
    carro.placaAntiga?.trim() &&
      (carro.tipo === 'SUBSTITUICAO_VEICULO' || carro.tipo === 'SUBSTITUICAO_E_TROCA_TITULAR'),
  );
}

/* ─────────────────── divergências local × SGA (at-32) ─────────────────── */

/**
 * ⚠️ A tela antiga tratava **tudo que não era `ACEITA` como "Ignorada"**, o que
 * rotulava errado `RESOLVIDA_ENVIO` e `RETRATADA`. O mapa correto veio do
 * `ModalDetalhesDivergencia`, onde estava exportado.
 */
const POR_STATUS_DIVERGENCIA: Record<DivergenciaStatus, RotuloChip> = {
  ABERTA: { texto: 'Aberta', variante: 'pendente' },
  ACEITA: { texto: 'Aceita', variante: 'ok' },
  IGNORADA: { texto: 'Ignorada', variante: 'neutro' },
  RESOLVIDA_ENVIO: { texto: 'Resolvida no envio', variante: 'ok' },
  RETRATADA: { texto: 'Retratada', variante: 'neutro' },
};

export function rotuloStatusDivergencia(status: DivergenciaStatus | string): RotuloChip {
  return (
    POR_STATUS_DIVERGENCIA[status as DivergenciaStatus] ?? {
      texto: String(status),
      variante: 'neutro' as VarianteChip,
    }
  );
}

const POR_ORIGEM_DIVERGENCIA: Record<DivergenciaOrigem, string> = {
  IMPORTACAO: 'Importação',
  ON_ACCESS: 'Uso da tela',
  CRON: 'Rotina',
};

export function rotuloOrigemDivergencia(origem: DivergenciaOrigem | string): string {
  return POR_ORIGEM_DIVERGENCIA[origem as DivergenciaOrigem] ?? String(origem);
}

/** Valor vazio vira travessão — o branco na tela não se distingue de "não sei". */
export function valorOuTraco(valor: string | null | undefined): string {
  const texto = (valor ?? '').trim();
  return texto === '' ? '—' : texto;
}

/* ─────────────── log de envios ("Ver envio", at-11) ─────────────── */

/**
 * ⚠️ O modal mostrava os valores **crus** do banco: `VISTORIA_FOTOS`, `IMEDIATO`,
 * `SUCESSO`. São nomes de enum, não de interface.
 */
const POR_TIPO_LOG: Record<EnvioLogItem['tipo'], string> = {
  ASSOCIADO: 'Associado',
  VEICULO: 'Veículo',
  VISTORIA: 'Vistoria',
  VISTORIA_FOTOS: 'Fotos da vistoria',
};

export function rotuloTipoLog(tipo: EnvioLogItem['tipo'] | string): string {
  return POR_TIPO_LOG[tipo as EnvioLogItem['tipo']] ?? String(tipo);
}

export function rotuloStatusLog(status: EnvioLogItem['status'] | string): RotuloChip {
  if (status === 'SUCESSO') return { texto: 'Sucesso', variante: 'ok' };
  if (status === 'FALHA') return { texto: 'Falha', variante: 'perigo' };
  return { texto: String(status), variante: 'neutro' };
}

/**
 * 🔴 **NÃO reusar `rotuloOrigemExecucao` de `syncExecucao.ts`.** Aquele mapeia tudo
 * que não é `CRON` para "manual", e aqui existe **`IMEDIATO`** — o envio que
 * acontece no momento da ação (at-36), que não é nem cron nem clique do TI.
 * Confundir os dois apagaria justamente a distinção que o log existe para mostrar.
 */
const POR_ORIGEM_LOG: Record<string, string> = {
  CRON: 'cron',
  CRON_VINCULO_PRODUTOS: 'cron (vínculo de produtos)',
  MANUAL: 'manual',
  IMEDIATO: 'imediato (na ação)',
  IMPORTACAO: 'importação',
  DESCOBERTA: 'descoberta',
};

export function rotuloOrigemLog(origem: string | null | undefined): string {
  if (!origem) return '—';
  return POR_ORIGEM_LOG[origem] ?? origem;
}
